// src/lib/utils/safe-html.tsx
// Shared component for safely rendering rich text HTML including math-node tags.
// Used in: admin question view dialog, QuestionDisplay (student exam), results page,
//          and public question pages (/questions/...)

'use client';

import { useEffect, useState, useMemo } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

/**
 * Processes stored HTML and replaces <math-node> custom tags with
 * rendered KaTeX HTML before passing to DOMPurify for safe rendering.
 */
function processMathNodes(html: string): string {
  if (!html) return '';

  return html.replace(
    /<math-node([^>]*)><\/math-node>/g,
    (_, attrs) => {
      const latexMatch = attrs.match(/latex=(['"])(.*?)\1/);
      const displayMatch = attrs.match(/display=(['"])(.*?)\1/);
      let latex = ''
      if (latexMatch) {
        try {
          latex = decodeURIComponent(latexMatch[2])
        } catch {
          latex = latexMatch[2]
        }
      }
      const isDisplay = displayMatch ? displayMatch[2] === 'true' : false;

      if (!latex) return '';

      try {
        const rendered = katex.renderToString(latex, {
          throwOnError: false,
          displayMode: isDisplay,
          output: 'html',
        });

        return isDisplay
          ? `<div style="text-align:center;margin:8px 0;overflow-x:auto;">${rendered}</div>`
          : `<span style="display:inline-block;vertical-align:middle;">${rendered}</span>`;
      } catch {
        return `<span style="color:red;">[math error: ${latex}]</span>`;
      }
    }
  );
}

// ─── DOMPurify config ─────────────────────────────────────────────────────────

const DOMPURIFY_CONFIG = {
  USE_PROFILES: { html: true, svg: true, mathMl: true },
  ADD_TAGS: [
    'math', 'semantics', 'mrow', 'mi', 'mo', 'mn', 'mfrac',
    'msup', 'msub', 'msubsup', 'msqrt', 'mroot', 'mover',
    'munder', 'munderover', 'mtable', 'mtr', 'mtd', 'mtext',
    'annotation', 'annotation-xml'
  ],
  ADD_ATTR: [
    'encoding', 'display', 'fence', 'stretchy'
  ],
  FORCE_BODY: false,
}

// ─── DOMPurify singleton — loaded once, reused forever ───────────────────────

let DOMPurifyInstance: any = null
let purifyLoadPromise: Promise<void> | null = null

function loadPurify() {
  if (typeof window === 'undefined') return;
  if (purifyLoadPromise) return purifyLoadPromise;
  
  purifyLoadPromise = import('dompurify')
    .then((mod) => {
      DOMPurifyInstance = mod.default ?? mod
    })
    .catch(() => {
      console.warn('DOMPurify failed to load — rendering unsanitized HTML')
      DOMPurifyInstance = {
        sanitize: (html: string) => html // passthrough fallback
      }
    });
  return purifyLoadPromise;
}

function getSanitized(html: string): string | null {
  if (!DOMPurifyInstance) return null
  const withMath = processMathNodes(html)
  return DOMPurifyInstance.sanitize(withMath, DOMPURIFY_CONFIG)
}

// ─── Component ────────────────────────────────────────────────────────────────

interface SafeHtmlProps {
  html: string;
  className?: string;
}

export function SafeHtml({ html, className }: SafeHtmlProps) {
  const [ready, setReady] = useState(!!DOMPurifyInstance)

  useEffect(() => {
    if (DOMPurifyInstance) {
      setReady(true)
      return
    }
    const promise = loadPurify();
    if (promise) promise.then(() => setReady(true))
  }, [])

  const sanitized = useMemo(() => {
    if (!html || !ready) return ''
    return getSanitized(html) ?? ''
  }, [html, ready])

  const contentKey = useMemo(() => {
    if (!html) return 'empty';
    let hash = 0;
    for (let i = 0; i < html.length; i++) {
      hash = ((hash << 5) - hash) + html.charCodeAt(i);
      hash |= 0;
    }
    return `html-${hash}`;
  }, [html]);

  if (!html) return null

  if (!html.includes('<')) {
    return <span className={className}>{html}</span>
  }

  if (!ready) {
    // On the server (where DOMPurify isn't loaded), we render the math-processed HTML directly.
    // This ensures content is visible immediately and indexed by search engines.
    const withMath = processMathNodes(html)
    return (
      <div 
        key={contentKey}
        className={`rich-content ${className || ''}`} 
        dangerouslySetInnerHTML={{ __html: withMath }}
        suppressHydrationWarning 
      />
    )
  }

  return (
    <div
      key={contentKey}
      className={`rich-content ${className || ''}`}
      dangerouslySetInnerHTML={{ __html: sanitized }}
      suppressHydrationWarning
    />
  )
}